import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { origemConfiavel } from '../config/origensConfiaveis';
import { normalizarContextoTela } from '../config/contextoTelaChatIa';
import {
  deleteBtnMaisClickadoSession,
  deleteDepartamentoSelecionadoSession,
  getHeaderSidBarNovo,
  removerTelaAtual,
  removeTempSession
} from '../services/authentication/auth';

const MenuSidebarContext = createContext();

const calcWidthItens = width => [700, 850, 1000, 1200, 1400, 1600].findIndex(w => width < w) + 1 || 7;

const useControleItensVisiveis = () => {
  const [itens, setItens] = useState(() =>
    typeof window === 'undefined' ? 5 : calcWidthItens(window.innerWidth)
  );

  useEffect(() => {
    const onResize = () => setItens(calcWidthItens(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return itens;
};

export const MenuSidebarProvider = ({ children }) => {
  const [departamentoSelecionado, setDepartamentoSelecionado] = useState(false);
  const [mostrarSideBar, setMostrarSideBar] = useState(false);
  const [loaderGeralMain, setLoaderGeralMain] = useState(false);
  const [btnMaisClickado, setBtnMaisClickado] = useState(false);
  const [scrIframe, setScrIframe] = useState('');
  const [scrIframeConfiguracoes, setScrIframeConfiguracoes] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [valueTab, setValueTab] = useState(false);
  const [controleRecarregarTela, setControleRecarregarTela] = useState(false);
  const [isPendingMenuPrincipal, setIsPendingMenuPrincipal] = useState(false);
  const [temClienteSelecionado, setTemClienteSelecionado] = useState(false);
  const [contextoEsteira, setContextoEsteira] = useState(null);
  const [temAlteracoesPendentesEsteira, setTemAlteracoesPendentesEsteira] = useState(false);
  const [enviarMensagemEsteira, setEnviarMensagemEsteira] = useState(() => () => false);
  const [user, setUser] = useState(false);
  const [contextoTelaChatIa, setContextoTelaChatIa] = useState(null);
  const controleItensVisiveis = useControleItensVisiveis();

  useEffect(() => {
    const handleMessage = event => {
      if (!origemConfiavel(event.origin)) return;

      if (event.data?.type === 'CLIENTE_SELECIONADO') {
        setTemClienteSelecionado(event.data?.condition);
      }

      if (event.data?.type === 'ESTEIRA_CONTEXTO_PROPOSTA') {
        setContextoEsteira({
          codPpc: event.data?.codPpc,
          cnpj: event.data?.cnpj ?? null,
          statusProposta: event.data?.statusProposta ?? null,
          etapaAtual: event.data?.etapaAtual ?? null,
          etapasValidas: event.data?.etapasValidas ?? [],
          etapasBloqueadas: event.data?.etapasBloqueadas ?? []
        });
        setTemAlteracoesPendentesEsteira(Boolean(event.data?.temAlteracoesPendentes));
      }

      if (event.data?.type === 'ESTEIRA_ALTERACOES_PENDENTES') {
        setTemAlteracoesPendentesEsteira(Boolean(event.data?.temAlteracoesPendentes));
      }

      if (event.data?.type === 'ESTEIRA_PROPOSTA_LIMPA') {
        setContextoEsteira(null);
        setTemAlteracoesPendentesEsteira(false);
      }

      if (event.data?.type === 'IA_CONTEXTO_TELA') {
        const contextoNormalizado = normalizarContextoTela(event.data);
        if (contextoNormalizado) {
          setContextoTelaChatIa({ ...contextoNormalizado, recebidoDe: event.origin });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleDrawerToggle = () => {
    setMenuOpen(prev => !prev);
  };

  const esconderMenuPrincipal = () => {
    setBtnMaisClickado(false);
    setDepartamentoSelecionado(false);
    setMostrarSideBar(false);
    setValueTab(false);
    setScrIframe('');
    deleteDepartamentoSelecionadoSession();
    deleteBtnMaisClickadoSession();
    removeTempSession('btnMaisClickadoTemp');
    removeTempSession('departamentoTemp');
    removeTempSession('codDepartamentoTemp');
    removeTempSession('menu-atual');
    removeTempSession('projeto-atual');
    removeTempSession('rota-atual');
    removeTempSession('telaAbertaTemp');
  };

  const excluirTelaAtual = () => {
    removerTelaAtual();
    removerTelaAtual('-conf');

    setScrIframe('');
    setScrIframeConfiguracoes('');
  };

  const verificaHeaderSidebarNovo = () => {
    const className = 'header-older';
    const sessionHeader = !!getHeaderSidBarNovo();
    if (sessionHeader) {
      document.body.classList.remove(className);
      return true;
    } else {
      document.body.classList.add(className);
      return false;
    }
  };

  const value = useMemo(
    () => ({
      mostrarSideBar,
      setMostrarSideBar,
      scrIframe,
      setScrIframe,
      departamentoSelecionado,
      setDepartamentoSelecionado,
      loaderGeralMain,
      setLoaderGeralMain,
      menuOpen,
      setMenuOpen,
      handleDrawerToggle,
      valueTab,
      setValueTab,
      controleItensVisiveis,
      setBtnMaisClickado,
      btnMaisClickado,
      controleRecarregarTela,
      setControleRecarregarTela,
      scrIframeConfiguracoes,
      setScrIframeConfiguracoes,
      esconderMenuPrincipal,
      isPendingMenuPrincipal,
      setIsPendingMenuPrincipal,
      excluirTelaAtual,
      verificaHeaderSidebarNovo,
      temClienteSelecionado,
      setTemClienteSelecionado,
      contextoEsteira,
      setContextoEsteira,
      temAlteracoesPendentesEsteira,
      setTemAlteracoesPendentesEsteira,
      enviarMensagemEsteira,
      setEnviarMensagemEsteira,
      setUser,
      user,
      contextoTelaChatIa,
      setContextoTelaChatIa
    }),
    [
      mostrarSideBar,
      scrIframe,
      departamentoSelecionado,
      loaderGeralMain,
      menuOpen,
      valueTab,
      controleItensVisiveis,
      btnMaisClickado,
      controleRecarregarTela,
      scrIframeConfiguracoes,
      isPendingMenuPrincipal,
      temClienteSelecionado,
      contextoEsteira,
      temAlteracoesPendentesEsteira,
      enviarMensagemEsteira,
      user,
      contextoTelaChatIa
    ]
  );

  return <MenuSidebarContext.Provider value={value}>{children}</MenuSidebarContext.Provider>;
};

export const useMenuSidebar = () => {
  const context = useContext(MenuSidebarContext);
  if (!context) {
    throw new Error('useMenuSidebar deve ser usado dentro de MenuSidebarProvider');
  }
  return context;
};
